import React, { useEffect, useMemo, useState } from "react";
import {
  fetchAdminUnitsAll,
  fetchAdminRooms,
  fetchAdminTenanciesAll,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import {
  formatOccupancyStatusDe,
  getRoomOccupancyStatus,
  isActiveRoom,
} from "../../utils/unitOccupancyStatus";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../../utils/unitDisplayId";
import CoLivingPageHeader from "./rooms/CoLivingPageHeader";
import CoLivingToolbar, {
  ALL_PROPERTIES,
  ALL_STATUSES,
} from "./rooms/CoLivingToolbar";
import CoLivingKpiGrid from "./rooms/CoLivingKpiGrid";
import CoLivingUnitsSection from "./rooms/CoLivingUnitsSection";
import CoLivingOccupancyCalendar from "./rooms/CoLivingOccupancyCalendar";
import CoLivingPageSkeleton from "./rooms/CoLivingPageSkeleton";
import { splitUnitAddress } from "./rooms/roomDisplayHelpers";
import {
  DEFAULT_MONTH_COUNT,
  buildOccupancyCalendarModel,
} from "./rooms/occupancyCalendarModel";

/**
 * The deposit security of the tenancy that is living in this room TODAY.
 *
 * ONE BINDING, AND IT IS THE ROOM ROW'S OWN ANSWER. `current_tenancy_id` is
 * decided by `compute_room_planning` on the backend — the same owner that
 * decides the availability sentence on the card — and this looks up exactly
 * that id. It never searches by tenant, by room, by status, by move-in date or
 * by array position: each of those picks a plausible row, and a plausible row
 * is how a card ends up printing one tenant's deposit under another tenant's
 * name. A tenant may hold two tenancies and a room has usually carried
 * several; only the id separates them.
 *
 * NOTHING IS CLASSIFIED HERE. The state travels exactly as the backend derived
 * it — `derive_tenant_security_state`, the same classifier /admin/kautionen
 * reads — together with the deposit type its wording depends on. This does not
 * consult `deposit_received`, compare an amount against zero, or decide what
 * "cash" means. Those were the three re-derivations that had two screens
 * disagreeing about one tenancy before K4x.2, and a third copy here would be
 * the one nobody notices has drifted.
 *
 * FAILS CLOSED, TWICE. A room with no current tenancy has no deposit question
 * to answer; a `current_tenancy_id` whose row is not in the list this page
 * loaded is a question this page cannot answer. Both return `null` rather than
 * the nearest row, and the card renders the same em dash it renders for an
 * unknown tenant.
 *
 * ENDED AND CANCELLED TENANCIES CANNOT ARRIVE HERE, and not because this
 * function filters them: `load_room_planning_projection` only ever considers
 * `active` and `reserved` rows, so an ended tenancy is never named as
 * `current_tenancy_id` in the first place. A room whose last tenant moved out
 * reads as free, which is what it is.
 */
function roomDepositSecurity(room, tenancyById) {
  const currentId = room?.current_tenancy_id;
  if (!currentId) return null;
  const tenancy = tenancyById.get(String(currentId));
  if (!tenancy) return null;
  return {
    state: tenancy.security_state,
    depositType: tenancy.tenant_deposit_type,
  };
}

/** A section rule, the shape the Einheiten page uses above its own blocks. */
function SectionHeading({ title, subtitle }) {
  return (
    <div className="mb-[10px]">
      <div className="flex items-center gap-2">
        {/* A real heading, not a styled span: the new units section opens with
            an <h2>, and a page whose other two section labels are spans has a
            document outline that skips straight from the <h1> to an <h3>.
            Same classes, so nothing moves. */}
        <h2 className="m-0 text-[10px] font-medium uppercase tracking-[0.8px] text-muted-foreground">
          {title}
        </h2>
        <div className="h-px flex-1 bg-border" />
      </div>
      {subtitle ? (
        <p className="m-0 mt-1 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>
      ) : null}
    </div>
  );
}

/**
 * Every room name in the org, grouped by the unit it belongs to (UI1).
 *
 * ONLY REAL NAMES. `roomDisplayName` falls back to "Zimmer 3" for a room that
 * has none, and that placeholder is not a name — it is a position in whatever
 * array happened to be rendered. Indexing it would let a search for "Zimmer 3"
 * match a different room in every unit, so an unnamed room simply contributes
 * nothing and stays findable through its unit.
 *
 * NOT FILTERED TO CO-LIVING, and it does not need to be: the predicate below
 * is only ever asked about units that are already in the Co-Living candidate
 * list, so a room hanging off an apartment can never be consulted. Keeping the
 * index independent of that filter is what lets it be built here, above the
 * search, without reordering anything else on the page.
 *
 * ONE PASS over the rooms already in hand. No request, no second grouping
 * rule — `unitRoomIndex` further down still owns the grouping that the unit
 * rows and the calendar are drawn from.
 */
function buildRoomNameIndex(rooms) {
  const byUnit = new Map();
  (rooms ?? []).forEach((room) => {
    const key = String(room?.unitId ?? room?.unit_id ?? "");
    if (!key) return;
    const name = String(room?.roomName ?? room?.name ?? "").trim();
    if (!name) return;
    if (!byUnit.has(key)) byUnit.set(key, []);
    byUnit.get(key).push(name.toLowerCase());
  });
  return byUnit;
}

/**
 * Does this unit match the search box?
 *
 * UI1 WIDENED THIS, ADDITIVELY. Every unit field that matched before still
 * matches; a room name is a second way in. The two are OR'd because they are
 * the same question asked twice — "is this the thing I typed?" — and a manager
 * who remembers a room but not its unit was previously left with an empty page.
 *
 * THE OTHER FILTERS ARE A DIFFERENT AXIS AND STAY AND'ed. A unit whose room
 * name matches but whose property is filtered out stays hidden: the property
 * and status selects narrow what the search found, they do not compete with it.
 * That happens at the call sites below, which are unchanged.
 */
function unitMatchesRoomPageSearch(unit, rawQ, roomNamesByUnit = null) {
  const q = String(rawQ || "").trim().toLowerCase();
  if (!q) return true;
  const nu = normalizeUnit(unit);
  const blob = [
    getUnitHeadingLabel(nu),
    getUnitAddressSubtitle(nu),
    nu.id,
    nu.unitId,
    nu.city,
    nu.place,
    nu.zip,
    nu.postal_code,
    nu.address,
    nu.address_line1,
    nu.shortUnitId,
    nu.short_unit_id,
  ]
    .filter((x) => x != null && String(x).trim() !== "")
    .join(" ")
    .toLowerCase();
  if (blob.includes(q)) return true;

  if (!roomNamesByUnit) return false;
  const names =
    roomNamesByUnit.get(String(nu.unitId || nu.id || "")) ??
    roomNamesByUnit.get(String(unit?.id || "")) ??
    null;
  if (!names) return false;
  return names.some((name) => name.includes(q));
}

/**
 * Does this room belong in the chosen status filter?
 *
 * "FREI" IS THE ONLY ONE OF THE THREE THAT IS A CLAIM ABOUT LETTABILITY, and
 * that is why only it carries the extra condition. `getRoomOccupancyStatus`
 * answers belegt | reserviert | frei and has no branch for a room that has been
 * stood down; a retired room carries no tenancy, so it arrives as "frei" and
 * used to come back from a "Frei" filter — beside a card that OC4b-1 had
 * already corrected to read "Inaktiv". One screen, two contradicting answers.
 *
 * "Belegt" and "Reserviert" need nothing: a retired room cannot classify as
 * either, so adding an activity term there would be dead weight pretending to
 * be symmetry. "Alle Status" never reaches this predicate at all — the retired
 * room stays visible as inventory on the page that administers it.
 *
 * THE TWO TRUTHS STAY SEPARATE. Occupancy is `getRoomOccupancyStatus`,
 * activity is `isActiveRoom`, and this function combines their answers without
 * either knowing about the other. Teaching the shared occupancy helper an
 * "inaktiv" state instead would move fourteen consumers in one step — the
 * calendar, the room map, the unit detail lists, both metric folds and four
 * surfaces that are already correct.
 *
 * `status` IS PASSED IN rather than computed, because the two call sites get it
 * differently: the top-level list derives it per room, and the per-unit list
 * reads it from `statusByRoomId`, which decided it once. Both must apply the
 * same rule, or a filter could narrow the page while a unit row let the room
 * back in.
 */
function roomMatchesStatusFilter(room, status, statusFilter) {
  if (status !== statusFilter) return false;
  if (statusFilter === "frei") return isActiveRoom(room);
  return true;
}

function AdminRoomsPage() {
  // `null` means NOT YET KNOWN, `[]` means loaded and genuinely empty. The
  // distinction is load-bearing: `getRoomOccupancyStatus` reads an empty
  // tenancy array as "this room has no tenancy", i.e. frei. Seeding these
  // with `[]` therefore made the whole loading window — and every failed
  // request, which used to be swallowed into `[]` — render as a confident
  // "all rooms free".
  const [units, setUnits] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [tenancies, setTenancies] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [unitSearch, setUnitSearch] = useState("");
  const [propertyFilter, setPropertyFilter] = useState(ALL_PROPERTIES);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [mapOpenByUnit, setMapOpenByUnit] = useState({});
  // `calendarOpenByUnit` is gone with the per-unit calendar accordions: the
  // matrix shows every unit at once, so there is nothing left to open.
  const [calendarMonthCount, setCalendarMonthCount] = useState(DEFAULT_MONTH_COUNT);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    setUnits(null);
    setRooms(null);
    setTenancies(null);

    // All three are required to say anything true about occupancy, so they
    // resolve together: a partial success would let the page show units and
    // rooms while silently guessing at their status.
    Promise.all([
      fetchAdminUnitsAll(),
      fetchAdminRooms(),
      fetchAdminTenanciesAll(),
    ])
      .then(([unitRows, roomRows, tenancyRows]) => {
        if (cancelled) return;
        setUnits(Array.isArray(unitRows) ? unitRows.map(normalizeUnit) : []);
        setRooms(Array.isArray(roomRows) ? roomRows.map(normalizeRoom) : []);
        setTenancies(Array.isArray(tenancyRows) ? tenancyRows : []);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(
          "Die Co-Living-Daten konnten nicht geladen werden. Belegung und Verfügbarkeit werden deshalb nicht angezeigt."
        );
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const isLoading = !loadError && (units === null || rooms === null || tenancies === null);

  const coLivingUnits = useMemo(() => {
    return (units ?? []).filter((unit) => unit.type === "Co-Living");
  }, [units]);

  /**
   * The properties the loaded units actually belong to.
   *
   * Derived from the units already in hand — `property_id` and
   * `property_title` ride along on every row of `GET /api/admin/units` — so
   * the filter costs no request. Units without a property simply contribute
   * no option; they stay visible under "Alle Liegenschaften" and are hidden
   * by any specific choice, which is what picking a property means.
   */
  const propertyOptions = useMemo(() => {
    const byId = new Map();
    coLivingUnits.forEach((u) => {
      const id = String(u.property_id || "").trim();
      if (!id || byId.has(id)) return;
      byId.set(id, String(u.property_title || "").trim() || id);
    });
    return Array.from(byId, ([value, label]) => ({ value, label }));
  }, [coLivingUnits]);

  /** Room names by unit, for the search predicate (UI1). Rebuilt only when the
   *  rooms themselves change, never per keystroke. */
  const roomNamesByUnit = useMemo(() => buildRoomNameIndex(rooms), [rooms]);

  // Search -> property -> rooms -> status. Each step narrows the one before it.
  const searchedUnits = useMemo(
    () =>
      coLivingUnits.filter((u) =>
        unitMatchesRoomPageSearch(u, unitSearch, roomNamesByUnit)
      ),
    [coLivingUnits, unitSearch, roomNamesByUnit]
  );

  const propertyFilteredUnits = useMemo(() => {
    if (propertyFilter === ALL_PROPERTIES) return searchedUnits;
    return searchedUnits.filter(
      (u) => String(u.property_id || "") === String(propertyFilter)
    );
  }, [searchedUnits, propertyFilter]);

  /** The rooms the two sections below are allowed to draw. */
  const visibleRooms = useMemo(() => {
    const ids = new Set(
      propertyFilteredUnits.map((u) => u.unitId || u.id).filter(Boolean)
    );
    const inScope = (rooms ?? []).filter((room) =>
      ids.has(room.unitId ?? room.unit_id)
    );
    if (statusFilter === ALL_STATUSES) return inScope;
    return inScope.filter((room) =>
      roomMatchesStatusFilter(
        room,
        getRoomOccupancyStatus(room, tenancies),
        statusFilter
      )
    );
  }, [rooms, propertyFilteredUnits, statusFilter, tenancies]);

  /** With a status chosen, a unit whose rooms all fell away has nothing to show. */
  const visibleCoLivingUnits = useMemo(() => {
    if (statusFilter === ALL_STATUSES) return propertyFilteredUnits;
    const unitIdsWithRooms = new Set(
      visibleRooms.map((r) => String(r.unitId ?? r.unit_id))
    );
    return propertyFilteredUnits.filter((u) =>
      unitIdsWithRooms.has(String(u.unitId || u.id))
    );
  }, [propertyFilteredUnits, visibleRooms, statusFilter]);

  // NO open-state reset on filter change (Slice 3).
  //
  // An effect used to live here that rewrote both open-state maps to all-false
  // whenever `visibleCoLivingUnits` changed identity — which is every
  // keystroke in the search box and every filter change. That is the T8
  // defect: opening a unit and then typing collapsed it again, even though the
  // unit was still on screen. Units default to closed through
  // `?? false` at the read site, so nothing needs to seed them, and a unit that
  // the filter hides keeps its entry so that clearing the filter brings it
  // back as the user left it.

  /**
   * Every Co-Living room in the org — the KPI population.
   *
   * Deliberately NOT the filtered set: the four cards are a management
   * header describing the whole portfolio, and a filter that moved them
   * would make "Frei 4" mean something different on every click.
   */
  const coLivingRooms = useMemo(() => {
    const ids = new Set(
      coLivingUnits.map((u) => u.unitId || u.id).filter(Boolean)
    );
    return (rooms ?? []).filter((room) => ids.has(room.unitId ?? room.unit_id));
  }, [rooms, coLivingUnits]);

  /**
   * Every tenancy the page already loaded, keyed by its OWN id.
   *
   * Built once per tenancy list, not searched per room. A `.find()` inside the
   * room loop is O(rooms × tenancies), which is invisible on the fixtures and
   * measurable on a portfolio that has a few hundred of each.
   *
   * KEYED BY `tenancy.id` AND BY NOTHING ELSE — not by tenant, not by room.
   * Those are precisely the keys that return the wrong row, because neither is
   * unique: the only question this map answers is "which tenancy is THIS one",
   * and the room row already names it.
   *
   * NO REQUEST. `tenancies` is the list the page has loaded since before this
   * card existed, and every deposit field below rides on it already.
   */
  const tenancyById = useMemo(() => {
    const byId = new Map();
    (tenancies ?? []).forEach((t) => {
      const id = t?.id == null ? "" : String(t.id);
      if (id) byId.set(id, t);
    });
    return byId;
  }, [tenancies]);

  /**
   * Every room of every Co-Living unit, grouped by unit, with its occupancy
   * decided once.
   *
   * Two things depend on this being the FULL set rather than the filtered one:
   * the badge row on each unit, and the "4er WG" metadatum on each card. Both
   * describe the unit itself, so a status filter that showed two of four rooms
   * must not restate the unit as a two-room flat-share. The filtered subset is
   * derived from this below, never the other way round.
   */
  const unitRoomIndex = useMemo(() => {
    const byUnit = new Map();
    const statusByRoomId = new Map();
    const depositByRoomId = new Map();
    coLivingRooms.forEach((room) => {
      const key = String(room.unitId ?? room.unit_id ?? "");
      if (!key) return;
      if (!byUnit.has(key)) byUnit.set(key, []);
      byUnit.get(key).push(room);
      const roomKey = String(room.roomId ?? room.id);
      statusByRoomId.set(roomKey, getRoomOccupancyStatus(room, tenancies));
      // Decided in the same pass the status is decided in, so the two answers
      // on one card are always read off the same room row.
      depositByRoomId.set(roomKey, roomDepositSecurity(room, tenancyById));
    });
    return { byUnit, statusByRoomId, depositByRoomId };
  }, [coLivingRooms, tenancies, tenancyById]);

  /** The unit rows the section renders, each carrying both room sets. */
  const unitRowItems = useMemo(() => {
    const { byUnit, statusByRoomId, depositByRoomId } = unitRoomIndex;
    return visibleCoLivingUnits.map((unit) => {
      const unitId = String(unit.unitId || unit.id);
      const all = byUnit.get(unitId) ?? [];
      const shown =
        statusFilter === ALL_STATUSES
          ? all
          : all.filter((room) =>
              roomMatchesStatusFilter(
                room,
                statusByRoomId.get(String(room.roomId ?? room.id)),
                statusFilter
              )
            );
      // CURRENT CAPACITY, NOT THE ROW SET (OC2b / D4-B). `all` and `shown` stay
      // complete, so every room card keeps rendering and a retired room stays
      // administrable from the page that administers it. Only the four numbers
      // in the badge row are a statement about capacity TODAY, and a room that
      // has been stood down is not capacity — it would otherwise arrive as a
      // free room, because a retired room has no tenancy and therefore
      // classifies as "frei".
      const active = all.filter(isActiveRoom);
      const countBy = (s) =>
        active.filter(
          (room) => statusByRoomId.get(String(room.roomId ?? room.id)) === s
        ).length;
      return {
        unit,
        unitId,
        displayId: getUnitHeadingLabel(unit),
        unitRooms: all,
        visibleRooms: shown,
        statusByRoomId,
        depositByRoomId,
        counts: {
          total: active.length,
          occupied: countBy("belegt"),
          reserved: countBy("reserviert"),
          free: countBy("frei"),
        },
        isOpen: mapOpenByUnit[unitId] ?? false,
        onToggle: () =>
          setMapOpenByUnit((prev) => ({ ...prev, [unitId]: !(prev[unitId] ?? false) })),
        detailPath: `/admin/units/${encodeURIComponent(unitId)}`,
      };
    });
  }, [visibleCoLivingUnits, unitRoomIndex, statusFilter, mapOpenByUnit]);

  /**
   * The occupancy matrix, over the SAME groups the units section renders.
   *
   * Reusing `unitRowItems` is what keeps the two sections honest with each
   * other: the same search, the same property filter, the same status filter
   * and the same order, so a unit cannot appear in one and not the other. The
   * status filter narrows which ROOMS are rows — it never touches what a month
   * cell says, because the month states come from the backend's
   * `lifecycle_calendar` and are not a function of today's occupancy.
   */
  const occupancyCalendarModel = useMemo(
    () =>
      buildOccupancyCalendarModel({
        groups: unitRowItems.map((item) => ({
          unitId: item.unitId,
          displayId: item.displayId,
          addressLine1: splitUnitAddress(item.unit).line1,
          addressLine2: splitUnitAddress(item.unit).line2,
          rooms: item.visibleRooms,
        })),
        tenancies,
        monthCount: calendarMonthCount,
        occupancyLabelFor: (room) => {
          const st = unitRoomIndex.statusByRoomId.get(String(room.roomId ?? room.id));
          return st ? formatOccupancyStatusDe(st) : "—";
        },
      }),
    [unitRowItems, tenancies, calendarMonthCount, unitRoomIndex]
  );

  /**
   * The footnote's room figure — ACTIVE rooms among the rendered ones (OC2b).
   *
   * Counted from `unitRowItems` rather than from the folded model, because the
   * model's room objects keep only what a cell needs (`key`, `name`, `meta`,
   * `cells`, `aggregates`) and `is_active` is not among them. Reading it here
   * leaves the calendar's own business logic untouched: same rows, same cells,
   * same lifecycle states, same month axis — only the count beneath it changes.
   *
   * It follows `visibleRooms`, so the status filter keeps narrowing it exactly
   * as before.
   *
   * KNOWN CONSEQUENCE, left for a presentation slice: a retired room is still
   * rendered as a matrix row (removing rows is explicitly out of scope here), so
   * on a unit with one the footnote now counts one fewer than the rows beneath
   * it. Whether such a row should be labelled, dimmed or dropped is a
   * presentation question (OC4), and inventing an answer for it here would be
   * the wrong slice making it.
   */
  const calendarRoomCount = useMemo(
    () =>
      unitRowItems.reduce(
        (n, item) => n + item.visibleRooms.filter(isActiveRoom).length,
        0
      ),
    [unitRowItems]
  );

  const roomStats = useMemo(() => {
    // THE CURRENT CO-LIVING CAPACITY (OC2b / D4-A). `coLivingRooms` is still
    // every room row of every co-living unit — the room cards and the unit
    // badges are drawn from it — but the four cards up top are a statement
    // about how many rooms the portfolio HAS and how they stand today, and a
    // retired room is in neither number.
    const activeRooms = coLivingRooms.filter(isActiveRoom);
    const occupied = activeRooms.filter(
      (room) => getRoomOccupancyStatus(room, tenancies) === "belegt"
    ).length;
    const reserved = activeRooms.filter(
      (room) => getRoomOccupancyStatus(room, tenancies) === "reserviert"
    ).length;
    const free = activeRooms.filter(
      (room) => getRoomOccupancyStatus(room, tenancies) === "frei"
    ).length;

    return {
      // UI1: the unit count the page already held. Deliberately the FULL
      // Co-Living population, exactly like the four room figures beside it —
      // the cards are a portfolio header, and a filter that moved them would
      // make "Einheiten 2" mean something different on every click.
      units: coLivingUnits.length,
      total: activeRooms.length,
      occupied,
      reserved,
      free,
    };
  }, [coLivingRooms, tenancies, coLivingUnits]);

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      <div className="flex flex-col gap-6">
        <CoLivingPageHeader />

        {loadError ? (
          <div
            role="alert"
            className="rounded-[12px] border border-destructive/30 bg-destructive/10 px-4 py-5"
          >
            <p className="m-0 text-[13px] font-medium text-destructive">{loadError}</p>
            <button
              type="button"
              onClick={() => setReloadToken((n) => n + 1)}
              className="mt-3 rounded-[8px] border border-destructive/40 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Erneut versuchen
            </button>
          </div>
        ) : isLoading ? (
          // UI1: the same `role="status"` and the same sentence, now behind a
          // layout that matches what is arriving. The ladder is untouched —
          // error still wins over loading, and both still win over the page.
          <CoLivingPageSkeleton />
        ) : (
          <>
          <CoLivingToolbar
            search={unitSearch}
            onSearchChange={setUnitSearch}
            propertyFilter={propertyFilter}
            onPropertyFilterChange={setPropertyFilter}
            propertyOptions={propertyOptions}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onReset={() => {
              setUnitSearch("");
              setPropertyFilter(ALL_PROPERTIES);
              setStatusFilter(ALL_STATUSES);
            }}
          />

          <div>
            <SectionHeading title="Kennzahlen" />
            <CoLivingKpiGrid stats={roomStats} />
          </div>

          <CoLivingUnitsSection
            units={unitRowItems}
            emptyMessage={
              coLivingUnits.length === 0
                ? "Keine Co-Living Units gefunden."
                : "Keine Co-Living Units für diese Suche."
            }
          />

          <CoLivingOccupancyCalendar
            model={occupancyCalendarModel}
            monthCount={calendarMonthCount}
            onMonthCountChange={setCalendarMonthCount}
            roomCount={calendarRoomCount}
            emptyMessage={
              coLivingUnits.length === 0
                ? "Keine Co-Living Units gefunden."
                : "Keine Co-Living Units für diese Suche."
            }
          />

          </>
        )}
      </div>
    </div>
  );
}

export default AdminRoomsPage;
